import React, { useState, useEffect, useRef } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import { FroalaRichText } from "../../util/FroalaRichtext";

import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import { AutosaveDelay, CommentKey } from "../../../constant";
import { saveDapCommentData } from "../../../redux/slices/saveCommentSlice";
import {
  updateDapCommentData,
  DapComment,
} from "../../../redux/slices/updateDapCommentSlice";
import "froala-editor/js/froala_editor.pkgd.min.js";
import "froala-editor/js/plugins.pkgd.min.js";
import "froala-editor/js/third_party/embedly.min.js";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/third_party/embedly.min.css";
import Skeleton from "@mui/material/Skeleton";

// for grid
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "left",
  color: theme.palette.text.secondary,
}));

export const SwotGlobalEditForm = () => {
  const dispatch = useAppDispatch();
  const assetPlanId = useAppSelector(
    (state) => state.getAssetPlanId.assetPlanId
  );
  const swotStatus = useAppSelector((state) => state.dapComments?.loading);
  
  // Asset SWOT comments
  const assetStrengthComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "asset_strength"
  );
  const assetWeaknessComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "asset_weakness"
  );
  const assetOpportunitiesComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "asset_opportunities"
  );
  const assetThreatsComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "asset_threats"
  );
  
  // Sustainability SWOT comments
  const sustainabilityStrengthComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "sustainability_strength"
  );
  const sustainabilityWeaknessComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "sustainability_weakness"
  );
  const sustainabilityOpportunitiesComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "sustainability_opportunities"
  );
  const sustainabilityThreatsComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "sustainability_threats"
  );
  
  // Leasing SWOT comments
  const leasingStrengthComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "leasing_strength"
  );
  const leasingWeaknessComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "leasing_weakness"
  );
  const leasingOpportunitiesComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "leasing_opportunities"
  );
  const leasingThreatsComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "leasing_threats"
  );
  
  // Market SWOT comments
  const marketStrengthComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "market_strength"
  );
  const marketWeaknessComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "market_weakness"
  );
  const marketOpportunitiesComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "market_opportunities"
  );
  const marketThreatsComment = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === "market_threats"
  );

  const handleModelChange = (commentKey: string) => (model: string) => {
    try {
      const commentData: DapComment = {
        id: 0,
        assetPlanId: assetPlanId,
        commentKey: commentKey,
        commentText: model || "",
        sectionName: "SWOT"
      };
      dispatch(updateDapCommentData(commentData));
      setTimeout(() => {
        dispatch(saveDapCommentData(commentData));
      }, AutosaveDelay.DELAYTIME);
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  return (
    <div>
      <div className="tab-heading text-center">
        <h3 className="heading">SWOT Analysis</h3>
      </div>

      <h3 className="text-left sub-heading">Asset</h3>

      <Box sx={{ flexGrow: 1 }} className="summary-form m-20">
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Strengths</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={assetStrengthComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Asset_Strength")}
                  />
                </div>
              </Item>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Weakness</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={assetWeaknessComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Asset_Weakness")}
                  />
                </div>
              </Item>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Opportunities</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={assetOpportunitiesComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Asset_Opportunities")}
                  />
                </div>
              </Item>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Threats</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={assetThreatsComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Asset_Threats")}
                  />
                </div>
              </Item>
            )}
          </Grid>
        </Grid>
      </Box>

      <h3 className="text-left sub-heading">Sustainability</h3>

      <Box sx={{ flexGrow: 1 }} className="summary-form m-20">
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Strengths</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={sustainabilityStrengthComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Sustainability_Strength")}
                  />
                </div>
              </Item>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Weakness</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={sustainabilityWeaknessComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Sustainability_Weakness")}
                  />
                </div>
              </Item>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Opportunities</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={sustainabilityOpportunitiesComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Sustainability_Opportunities")}
                  />
                </div>
              </Item>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Threats</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={sustainabilityThreatsComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Sustainability_Threats")}
                  />
                </div>
              </Item>
            )}
          </Grid>
        </Grid>
      </Box>

      <h3 className="text-left sub-heading">Leasing</h3>

      <Box sx={{ flexGrow: 1 }} className="summary-form m-20">
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Strengths</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={leasingStrengthComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Leasing_Strength")}
                  />
                </div>
              </Item>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Weakness</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={leasingWeaknessComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Leasing_Weakness")}
                  />
                </div>
              </Item>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Opportunities</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={leasingOpportunitiesComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Leasing_Opportunities")}
                  />
                </div>
              </Item>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Threats</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={leasingThreatsComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Leasing_Threats")}
                  />
                </div>
              </Item>
            )}
          </Grid>
        </Grid>
      </Box>

      <h3 className="text-left sub-heading">Market</h3>

      <Box sx={{ flexGrow: 1 }} className="summary-form m-20">
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Strengths</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={marketStrengthComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Market_Strength")}
                  />
                </div>
              </Item>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Weakness</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={marketWeaknessComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Market_Weakness")}
                  />
                </div>
              </Item>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Opportunities</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={marketOpportunitiesComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Market_Opportunities")}
                  />
                </div>
              </Item>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            {swotStatus ? (
              <Skeleton variant="rounded" width="100%" height={250} animation="wave" />
            ) : (
              <Item>
                <h3 className="text-left">Threats</h3>
                <div className="grid-item-box">
                  <FroalaRichText
                    model={marketThreatsComment?.commentText || ""}
                    handlesModelChange={handleModelChange("Market_Threats")}
                  />
                </div>
              </Item>
            )}
          </Grid>
        </Grid>
      </Box>

    </div>
  );
};